import { useLayoutEffect, Ref } from "preact/hooks";

import { cmdKey } from "../../foundation/utils/keyboard";

type Handler = () => void;

interface DialogControlsOptions {
    isOpen: boolean;
    onClose: Handler;
    onOpen: Handler;
}
function useDialogControls<T extends HTMLElement>(
    ref: Ref<T>,
    { isOpen, onClose, onOpen }: DialogControlsOptions
): void {
    // Only listen for outside‑click when open
    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleClick = (evt: MouseEvent | TouchEvent) => {
            const el = ref.current;
            if (el && !el.contains(evt.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [isOpen, onClose, ref]);

    useLayoutEffect(() => {
        const handleKeydown = (evt: KeyboardEvent) => {
            if (isOpen && evt.key === "Escape") {
                onClose();
            }

            if (cmdKey(evt) && (evt.key === "/" || evt.key === "?" || (evt.key === "7"))) {
                onOpen();
            }
        };

        document.addEventListener("keydown", handleKeydown, true);
        return () => {
            document.removeEventListener("keydown", handleKeydown, true);
        };
    }, [isOpen, onClose, onOpen]);
}

export default useDialogControls;
